'use client';

import { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SKILLS } from '@/lib/utils';
import { Code2, Server, Wrench } from 'lucide-react';
import { useSiteData } from '@/lib/dataCache';

type SkillItem = { name: string; level: number; icon?: string };
type ApiCategory = {
  id: string;
  category: string;
  items: SkillItem[];
  order: number;
};

type TabCategory = {
  key: string;
  label: string;
  items: SkillItem[];
  icon: typeof Code2;
};

const FALLBACK_ICONS = [Code2, Server, Wrench];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' as const } },
};

function getProficiencyLabel(percentage: number): string {
  if (percentage >= 90) return 'Expert';
  if (percentage >= 80) return 'Advanced';
  if (percentage >= 70) return 'Proficient';
  return 'Intermediate';
}

function getCategoryDescription(label: string): string {
  const normalized = label.toLowerCase();
  if (normalized.includes('front'))
    return 'Building beautiful, interactive user interfaces';
  if (normalized.includes('back'))
    return 'Architecting robust server-side systems and APIs';
  if (normalized.includes('tool') || normalized.includes('dev'))
    return 'The ecosystem that powers my daily workflow';
  if (normalized.includes('mobile'))
    return 'Cross-platform experiences that feel native';
  if (normalized.includes('data'))
    return 'Turning raw data into insight and product value';
  return 'Technologies I reach for in this domain';
}

function buildFallbackCategories(): TabCategory[] {
  return [
    { key: 'frontend', label: 'Frontend', items: SKILLS.frontend.map((s) => ({ name: s.name, level: s.percentage })), icon: Code2 },
    { key: 'backend', label: 'Backend', items: SKILLS.backend.map((s) => ({ name: s.name, level: s.percentage })), icon: Server },
    { key: 'tools', label: 'Tools', items: SKILLS.tools.map((s) => ({ name: s.name, level: s.percentage })), icon: Wrench },
  ];
}

export default function Skills() {
  const apiCategories = useSiteData<ApiCategory[]>('expertise', '/api/expertise', []);

  const tabs: TabCategory[] = useMemo(() => {
    if (!apiCategories || apiCategories.length === 0) {
      return buildFallbackCategories();
    }
    return [...apiCategories]
      .sort((a, b) => a.order - b.order)
      .map((c, i) => ({
        key: c.id,
        label: c.category,
        items: Array.isArray(c.items) ? c.items : [],
        icon: FALLBACK_ICONS[i % FALLBACK_ICONS.length],
      }));
  }, [apiCategories]);

  const [activeKey, setActiveKey] = useState<string>(tabs[0]?.key ?? '');

  useEffect(() => {
    if (tabs.length > 0 && !tabs.find((t) => t.key === activeKey)) {
      setActiveKey(tabs[0].key);
    }
  }, [tabs, activeKey]);

  const active = tabs.find((t) => t.key === activeKey) ?? tabs[0];
  const skills = active?.items ?? [];
  const totalSkills = tabs.reduce((acc, t) => acc + t.items.length, 0);

  return (
    <section id="skills" className="skills-section">
      <div className="skills-blob skills-blob-1" />
      <div className="skills-blob skills-blob-2" />

      <div className="section-container">
        <motion.div
          className="section-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <span className="section-label">Skills</span>
          <h2 className="section-title">My Technical Expertise</h2>
          <p className="section-subtitle">
            A curated set of technologies I&apos;ve mastered through years of building
            real-world products, from frontend craftsmanship to backend architecture.
          </p>
        </motion.div>

        {tabs.length > 0 && (
          <motion.div
            className="skills-tabs"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {tabs.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setActiveKey(cat.key)}
                  className={`skills-tab ${activeKey === cat.key ? 'skills-tab-active' : ''}`}
                  aria-pressed={activeKey === cat.key}
                >
                  <span className="skills-tab-icon">
                    <Icon size={18} />
                  </span>
                  <span className="skills-tab-label">{cat.label}</span>
                  <span className="skills-tab-count">{cat.items.length}</span>
                </button>
              );
            })}
          </motion.div>
        )}

        {active && (
          <AnimatePresence mode="wait">
            <motion.p
              key={active.key}
              className="skills-category-desc"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
            >
              {getCategoryDescription(active.label)}
            </motion.p>
          </AnimatePresence>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={activeKey}
            className="skills-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            {skills.map((skill) => (
              <motion.div
                key={skill.name}
                className="skill-card"
                variants={cardVariants}
                whileHover={{ y: -6 }}
              >
                <div className="skill-card-header">
                  <div className="skill-card-name-group">
                    <h3 className="skill-card-name">{skill.name}</h3>
                    {active && <span className="skill-card-category">{active.label}</span>}
                  </div>
                  <div className="skill-card-percentage">{skill.level}%</div>
                </div>

                <div className="skill-card-progress">
                  <motion.div
                    className="skill-card-progress-fill"
                    initial={{ width: 0 }}
                    animate={{ width: `${skill.level}%` }}
                    transition={{ duration: 1.1, ease: 'easeOut', delay: 0.15 }}
                  >
                    <span className="skill-card-progress-glow" />
                  </motion.div>
                </div>

                <div className="skill-card-footer">
                  <span className="skill-card-level">
                    <span className="skill-card-level-dot" />
                    {getProficiencyLabel(skill.level)}
                  </span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        <motion.div
          className="skills-summary"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6 }}
        >
          <div className="skills-summary-item">
            <div className="skills-summary-value">{totalSkills}+</div>
            <div className="skills-summary-label">Technologies</div>
          </div>
          <div className="skills-summary-divider" />
          <div className="skills-summary-item">
            <div className="skills-summary-value">3+</div>
            <div className="skills-summary-label">Years of Practice</div>
          </div>
          <div className="skills-summary-divider" />
          <div className="skills-summary-item">
            <div className="skills-summary-value">50+</div>
            <div className="skills-summary-label">Projects Shipped</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
